"use client";

import styles from "./Flex.module.scss";
import classNames from "classnames";
import { FlexTypes } from "./Flex.types";
import { ElementType } from "react";

/**
 * Flex Component
 *
 * A flexible layout component that wraps CSS Flexbox functionality.
 * Provides a declarative API for creating responsive layouts with proper
 * alignment, spacing, and direction control.
 *
 * Features:
 * - Configurable flex direction (row, column, reverse variants)
 * - Alignment control (align-items and justify-content)
 * - Customizable gap between items
 * - Semantic HTML tag selection
 * - Accessibility support with ARIA attributes
 * - Click event handling
 *
 * @param props - FlexTypes interface containing component configuration
 * @returns JSX.Element - Flexible layout container
 */
const Flex = ({
  children,
  direction = "row",
  align,
  justify,
  gap = 0,
  className = "",
  style = {},
  ariaLabel,
  tag = "div",
  role,
  onClick,
}: FlexTypes) => {
  // Use proper typing for dynamic HTML tag rendering
  const HtmlTag = tag as ElementType;

  // Build CSS class names based on props
  const flexClasses = classNames(
    styles.comp,
    className,
    // Apply direction class if specified
    direction && styles[`comp--direction-${direction}`],
    // Apply alignment class if specified
    align && styles[`comp--align-${align}`],
    // Apply justify class if specified
    justify && styles[`comp--justify-${justify}`]
  );

  // Combine gap with custom styles
  const combinedStyles = {
    gap: gap,
    ...style,
  };

  return (
    <HtmlTag
      className={flexClasses}
      style={combinedStyles}
      role={role}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {children}
    </HtmlTag>
  );
};

export { Flex };
